import {
	BaseListContainer,
	BaseListHeaderContainer,
	BaseListInfoArea,
	BaseListFooterContainer,
	CommonListProps,
	EditQuizNoResults,
} from "./OptionListsComponents";
import i18nPackage from "../../../i18n/i18n";
import sharedStyles from "./OptionListsComponents.module.css";
import styles from "./BlockApplicationList.module.css";
import { CoreList, CoreListGroup, CoreListRow, OverflowText } from "../../../core";
import { EditQuizFilterType } from "../../components/EditQuizFilter";
import { Typography } from "@mui/material";
import { memo, useCallback, useId, useMemo, useState } from "react";
import { Application } from "@verity/proctoring-option/types";
import { useFocusElementOnMount } from "../../../hooks";
import AppUnknown from "../../images/configuration/app-unknown.svg?react";
import AddProcessNameModal from "../option-modals/AddApplicationModal";
import { Trans } from "react-i18next";
import useGroupedList from "../hooks/useGroupedList";
import { GroupedListSelectAllRow, GroupedListToolbar } from "./GroupedList";
import { PROCTORING_OPTION_TEXT } from "../option-text";
import { parseApplication } from "../../confirmation/confirmationHelper";

const ApplicationIcon = memo(function ApplicationIcon({ application }: { application: Application }) {
	if (application.type !== "Other" && application.iconSvg) {
		return <div className={styles.applicationIcon} dangerouslySetInnerHTML={{ __html: application.iconSvg }} />;
	}
	return <AppUnknown className={styles.applicationIcon} />;
});

const MemoizedApplicationRow = memo(function MemoizedApplicationRow({
	application,
	checked,
	onSelect,
}: {
	application: Application;
	checked: boolean;
	onSelect: (selected: boolean) => void;
}) {
	return (
		<CoreListRow
			label={
				<OverflowText className={styles.applicationName} component={"span"}>
					{application.name}
				</OverflowText>
			}
			icon={<ApplicationIcon application={application} />}
			checked={checked}
			onSelect={onSelect}
		/>
	);
});

function getFilterLabel(filterType: EditQuizFilterType, mode: BlockApplicationListProps["action"]) {
	if (filterType === "all") {
		return i18nPackage.t("editQuizPage.blockedApplicationList.filter.all");
	}
	if (mode === "allow" && filterType === "selected") {
		return i18nPackage.t("editQuizPage.blockedApplicationList.filter.allowed");
	} else if (mode === "allow" && filterType === "notSelected") {
		return i18nPackage.t("editQuizPage.blockedApplicationList.filter.blocked");
	} else if (mode === "block" && filterType === "selected") {
		return i18nPackage.t("editQuizPage.blockedApplicationList.filter.blocked");
	}
	// Mode block and not selected
	return i18nPackage.t("editQuizPage.blockedApplicationList.filter.allowed");
}

type ApplicationCategory = Application["type"];

export type BlockApplicationListMode = "block" | "allow";

type BlockApplicationListProps = {
	initialSelectedProcessNames: string[];
	allApplications: Application[];
	onUpdate: (processNames: string[]) => void;
	action: BlockApplicationListMode;
} & CommonListProps;

export default function BlockApplicationList({
	allApplications,
	initialSelectedProcessNames,
	onUpdate,
	action,
	focusOnMount,
	onFocus,
}: BlockApplicationListProps) {
	const [showAddModal, setShowAddModal] = useState(false);
	const { ref } = useFocusElementOnMount<HTMLDivElement>(focusOnMount, onFocus);
	const controlId = useId();

	const initialCustomApplications = useMemo(
		() =>
			initialSelectedProcessNames
				.map((rawStr) => parseApplication(rawStr, allApplications))
				.filter((app) => app.type === "Other"),
		[allApplications, initialSelectedProcessNames],
	);

	const { actions, filter, items, state } = useGroupedList<Application, ApplicationCategory>({
		categoryExtractor: (app) => app.type,
		formatForParent: (app) => app.processName,
		initialItems: allApplications,
		initialSelected: initialSelectedProcessNames,
		isInitialSelected: (app, rawStrings) => app.processName.some((p) => rawStrings.includes(p)),
		keyExtractor: (app) => app.name,
		onUpdate,
		searchSelector: (app) => app.name,
		sortFunction: (a, b) => a.name.localeCompare(b.name),
		initialCustomItems: initialCustomApplications,
	});

	const { activeStatus, searchText, setActiveStatus, setSearchText } = filter;
	const { displayedItems, groupedItems } = items;

	const handleAddCustomApplication = useCallback(
		(newApp: Application) => {
			actions.handleAddNewItem(newApp);
			actions.handleCategoryExpand("Other", true);
		},
		[actions],
	);

	const ListIcon = PROCTORING_OPTION_TEXT["applicationBlockMode"]["icon"];

	return (
		<BaseListContainer ref={ref}>
			<BaseListHeaderContainer>
				<BaseListInfoArea
					title={i18nPackage.t(`editQuizPage.blockedApplicationList.${action}.title`)}
					description={
						<Typography className={sharedStyles.baseListDescription}>
							<Trans
								i18n={i18nPackage}
								i18nKey={`editQuizPage.blockedApplicationList.${action}.description`}
								components={{
									b: <strong />,
								}}
							/>
						</Typography>
					}
					icon={ListIcon ? <ListIcon /> : undefined}
					slotProps={{
						hint: {
							text: i18nPackage.t(`editQuizPage.blockedApplicationList.${action}.note`),
						},
					}}
				/>
				<GroupedListToolbar
					slotProps={{
						filter: {
							activeStatus,
							setActiveStatus,
							renderFilterLabel: (value) => getFilterLabel(value, action),
							ariaLabel: i18nPackage.t("editQuizPage.blockedApplicationList.selectAriaLabel"),
						},
						textfield: {
							controlId,
							placeholder: i18nPackage.t("editQuizPage.blockedApplicationList.searchPlaceholder"),
							searchText,
							setSearchText,
							totalResults: displayedItems.length,
						},
					}}
					filterLabel={i18nPackage.t("editQuizPage.blockedApplicationList.filterLabel")}
				/>
			</BaseListHeaderContainer>
			{groupedItems.length > 0 && (
				<GroupedListSelectAllRow
					checked={state.allSelected}
					onChange={actions.handleSelectAll}
					buttonText={i18nPackage.t("editQuizPage.blockedApplicationList.addCustomApplication")}
					badgeText={
						state.numSelected === 0
							? undefined
							: i18nPackage.t(`editQuizPage.blockedApplicationList.${action}.badgeText`, {
									count: state.numSelected,
								})
					}
					onClick={() => setShowAddModal(true)}
				/>
			)}
			<BaseListFooterContainer>
				{groupedItems.length === 0 ? (
					<EditQuizNoResults />
				) : (
					<CoreList id={controlId} maxHeight={302}>
						{groupedItems.map((group) => {
							const category = group.category;
							const numSelected = state.getNumItemsSelectedInCategory(group);
							return (
								<CoreListGroup
									key={category}
									title={category}
									isSelected={state.isAllItemsInCategorySelected(group)}
									onSelect={(selected) => actions.handleSelectAllInCategory(group.items, selected)}
									expanded={state.isCategoryExapnded(category)}
									onExpand={() => actions.handleCategoryExpand(category)}
									isIndeterminate={state.isCategoryIndeterminate(group)}
									slotProps={{
										...(numSelected !== 0 && {
											chip: {
												text: i18nPackage.t(`editQuizPage.blockedApplicationList.${action}.badgeText`, {
													count: numSelected,
												}),
											},
										}),
									}}
								>
									{group.items.map((application) => (
										<MemoizedApplicationRow
											key={application.name}
											application={application}
											checked={state.isItemSelected(application)}
											onSelect={(selected) => actions.handleToggleItem(application, selected)}
										/>
									))}
								</CoreListGroup>
							);
						})}
					</CoreList>
				)}
			</BaseListFooterContainer>
			{showAddModal && (
				<AddProcessNameModal
					action={action}
					onAddExtension={handleAddCustomApplication}
					setShowAddModal={setShowAddModal}
				/>
			)}
		</BaseListContainer>
	);
}
